// 261015 P4b-1 (D9/D12/D13, Bước 5) — thẻ "Giám khảo" ở đầu khung tập ⑤, dựng từ biên lai `script.qualityReview.critic` + `criticFreshness`. THUẦN (jest). KHÔNG import antd / react.
//
// Luật cứng (R7/C1):
//  - Chỉ biên lai v2 CÓ khối `critic` hợp lệ mới ra thẻ. Bản v1 / vắng / hình hỏng ⇒ `null` — KHÔNG BAO GIỜ suy ra "đạt".
//  - "đạt" chỉ nói khi bản sửa được nhận VÀ hết CRITICAL trong `findingsAfter ∪ unscopedAfter` (Q5, chốt sau BE-A §6.8); còn thì nói "còn K lỗi nặng".
//  - `PASSED` = "giám khảo không thấy lỗi nặng" — KHÔNG phải "tập hay". `UNCHECKED` = chưa kiểm (không đỏ, không "đạt").
//  - Biên lai là lịch sử bất biến của lần sinh. `criticFreshness` ≠ CURRENT ⇒ thêm dòng "nội dung đã đổi" + liệt kê (chỉ đọc) ý kiến của cảnh đã đổi — FE không băm: cảnh nào đã đổi = finding KHÔNG còn chip `CRITIC_FINDING` tương ứng ở `violations` (BE tính lúc đọc).
import type {
  C2ChainReceipt, C2CriticStatus, C2Finding, C2RejectReason, C2RepairRejectReason, C2Severity, C2UncheckedReason,
  CriticFreshness, EpisodeScriptRecord, QualityReviewCritic,
} from '../../../types/adaptation';
import type { C2ChainStopReason } from '../../../types/adaptationWorkbench';

export const CRITIC_STATUSES: readonly C2CriticStatus[] = ['PASSED', 'REPAIRED', 'REPAIR_REJECTED', 'UNRESOLVED', 'UNCHECKED'];

/** Lý do `UNCHECKED` (BE `C2UncheckedReason`) → câu dễ hiểu. Đổi enum BE thì đổi cả ở đây + test (test khoá đủ mã). */
export const UNCHECKED_REASON_TEXT: Record<C2UncheckedReason, string> = {
  STRUCTURE_FAILED: 'kịch bản đang hỏng cấu trúc (cảnh rỗng / lời kể không hợp lệ) nên chưa gọi giám khảo',
  SAME_VENDOR: 'giám khảo cùng hãng AI với model đã viết nên không đủ khách quan',
  WRITER_UNKNOWN: 'không biết model nào đã viết bản nháp nên không kiểm được "khác hãng"',
  CALL_FAILED: 'lời gọi giám khảo bị lỗi hoặc trả kết quả không đọc được',
  TEMPLATE_UNAVAILABLE: 'hệ thống chưa có mẫu prompt của giám khảo',
  INTERNAL_ERROR: 'lỗi nội bộ khi chạy giám khảo',
  CRITIC_VENDOR_UNKNOWN: 'không xác định được hãng của model giám khảo — không chấm để tránh cùng hãng với model viết',
};

/** Lý do không nhận bản sửa (BE `C2RepairRejectReason`). */
export const REPAIR_REJECT_TEXT: Record<C2RepairRejectReason, string> = {
  NEW_INTEGRITY_DEFECT: 'bản sửa làm thêm lỗi nhịp trống / thoại thiếu người nói',
  VO_GUTS: 'bản sửa làm hỏng lời kể (cảnh hết nội dung hoặc mất thoại)',
  NEW_LINT_ERROR: 'bản sửa gây thêm lỗi chặn ở các cổng kiểm tra',
  REPAIR_CALL_FAILED: 'lượt sửa cảnh bị lỗi',
  RECHECK_FAILED: 'lượt chấm lại bị lỗi nên không có gì xác nhận bản sửa',
  NEW_CRITICAL: 'chấm lại vẫn thấy lỗi nặng mới',
  NOT_REDUCED: 'số lỗi nặng không giảm sau khi sửa',
  REPAIR_CHECK_FAILED: 'bước kiểm bản sửa bị lỗi nên giữ bản gốc',
};

/** Vì sao một ý kiến của giám khảo bị code loại (BE `C2RejectReason`) — vẫn lưu trong biên lai, không giấu. */
export const FINDING_REJECT_TEXT: Record<C2RejectReason, string> = {
  MALFORMED: 'sai định dạng',
  UNKNOWN_SCENE: 'trỏ tới cảnh không có thật',
  UNKNOWN_SUBJECT: 'trỏ tới đối tượng không có trong danh sách kiểm',
  APPROVED_CONTENT: 'nói về nội dung đã được người duyệt',
  EVIDENCE_TOO_SHORT: 'trích dẫn quá ngắn để chứng minh',
  EVIDENCE_NOT_IN_ARTIFACT: 'trích dẫn không có trong bản đã chấm',
};

/** Mã lỗi của giám khảo (template `film_episode_critic_c2`) → câu. Mã lạ ⇒ giữ mã thô. */
export const FINDING_CODE_TEXT: Record<string, string> = {
  REQUIRED_MOMENT_MISSING: 'Thiếu khoảnh khắc bắt buộc',
  PLANT_NOT_DELIVERED: 'Chi tiết gieo chưa lên phim',
  APPEAL_NOT_ON_SCREEN: 'Nhịp hấp dẫn đã hứa chưa có trên phim',
  INTRO_MISUSED: 'Câu giới thiệu dùng sai chỗ',
  SPOILER: 'Lộ trước nội dung ngoài tập',
  NOTE_AS_FACT: 'Ghi chú biên kịch bị kể như sự việc',
  PROFILE_CONTRADICTED: 'Mâu thuẫn hồ sơ nhân vật',
  PROP_DETAIL_LOST: 'Mất chi tiết đạo cụ',
  RULE_BROKEN: 'Phá luật thế giới',
  INVENTED_FACT: 'Bịa chi tiết không có nguồn',
  NOT_IN_SOURCE: 'Chi tiết không có trong nguồn',
  FABRICATED: 'Chi tiết bịa đặt',
  SOURCE_CONTRADICTION: 'Mâu thuẫn với nguồn',
  CONTINUITY_BREAK: 'Đứt mạch với tập trước',
  SAFETY: 'Đổi nghĩa về đồng thuận / an toàn',
};

export const SEVERITY_TEXT: Record<C2Severity, string> = { CRITICAL: 'Nặng', MAJOR: 'Đáng kể', MINOR: 'Nhẹ' };

/** Một dòng finding để vẽ (chỉ đọc). */
export interface CriticFindingView {
  severity: C2Severity;
  severityLabel: string;
  codeLabel: string;
  /** "Cảnh 3" (theo vị trí trong kịch bản HIỆN TẠI) · `cảnh "id"` nếu cảnh không còn · "Cả tập" khi lỗi cấp tập. */
  where: string;
  /** 261023 E3-P3h4d1 — id cảnh (`scenePlanId`) để «Mở cảnh» / tra biên nhận bước ③; null = lỗi cả tập. */
  sceneId: string | null;
  message: string;
  evidence: string;
}

export type CriticCardTone = 'ok' | 'warn' | 'unknown';

export interface CriticCardDetails {
  /** Ý kiến lượt chấm đầu (trước khi sửa). Có cả khi bản sửa được nhận — để so. */
  firstPass: CriticFindingView[];
  /** Ý kiến còn lại sau lượt cuối (cùng nguồn với chip / số "K"). */
  remaining: CriticFindingView[];
  /** Cảnh đã gửi đi sửa / đã đổi thật / bị bỏ qua (vượt giới hạn 3 cảnh) — nhãn "Cảnh N". */
  targetScenes: string[];
  changedScenes: string[];
  skippedScenes: string[];
  /** Ý kiến code loại (kèm lý do) — gộp lượt 1 + lượt chấm lại. */
  rejected: Array<{ view: CriticFindingView; reasonText: string }>;
  modelText: string | null;
  callsText: string | null;
  /** 261023 E3-P3e (L13) — chuỗi vòng tự sửa của nhánh chuỗi NEW_STORY (từ `chain.rounds`); `[]` khi biên lai không có `chain`. */
  rounds: ChainRoundView[];
  /** 261023 E3-P3e — vì sao máy dừng sửa («Dừng sửa: không hội tụ» / «Đã sửa 2 vòng, còn N lỗi»); null khi không có hoặc đã sạch. */
  chainStop: string | null;
}

export interface CriticCard {
  status: C2CriticStatus;
  tone: CriticCardTone;
  /** Dòng chính (chữ chốt trong doc Phase). */
  headline: string;
  /** Dòng giải thích thêm (vd số ý kiến nhẹ hơn) — null nếu không có. */
  detail: string | null;
  /** Số lỗi nặng còn lại (CRITICAL của nguồn "còn lại"). */
  criticalLeft: number;
  /** "Đánh giá của lần viết máy" (hoặc "Chấm lại bằng AI") + model + số lượt chấm. */
  meta: string;
  /** 261023 E3-P3b2 — biên nhận nhỏ «Chấm lại bằng AI lúc 14:05 25/10 · <người>»; null khi biên lai là của lần viết máy. */
  rejudgeNote: string | null;
  /** Chỉ khi `criticFreshness` ≠ CURRENT. */
  freshnessNote: string | null;
  /** Ý kiến của cảnh đã đổi (không còn chip) — liệt kê chỉ-đọc dưới `freshnessNote`. */
  staleFindings: CriticFindingView[];
  details: CriticCardDetails;
}

export const FRESHNESS_NOTE = 'Nội dung đã đổi sau lần chấm — ý kiến của cảnh đã đổi thuộc bản trước';
export const CRITIC_META_PREFIX = 'Đánh giá của lần viết máy';
/** 261023 E3-P3b2 — biên lai do người bấm «Chấm lại bằng AI» (BE ghi `trigger: 'manual_rejudge'`). */
export const REJUDGE_META_PREFIX = 'Chấm lại bằng AI';

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const strs = (x: unknown): string[] => (Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : []);
const has = (table: object, key: unknown): key is string => typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key);

/** Chỉ nhận phần tử ĐÚNG hình tối thiểu (JSON trong DB có thể hỏng). */
const findingsOf = (x: unknown): C2Finding[] => (Array.isArray(x) ? x : [])
  .filter((f): f is C2Finding => isObj(f) && typeof f.severity === 'string' && typeof f.code === 'string' && typeof f.scenePlanId === 'string' && typeof f.message === 'string' && typeof f.evidence === 'string');

const isCritical = (f: C2Finding): boolean => f.severity === 'CRITICAL';

type SceneRefs = ReadonlyArray<{ scenePlanId?: string }> | null | undefined;

/** `Cảnh N` theo vị trí trong kịch bản hiện tại; id không còn ⇒ id thô trong ngoặc kép. */
export const sceneLabel = (id: string, scenes: SceneRefs): string => {
  const i = (scenes ?? []).findIndex((s) => s?.scenePlanId === id);
  return i >= 0 ? `Cảnh ${i + 1}` : `cảnh "${id}"`;
};

export const findingView = (f: C2Finding, scenes: SceneRefs): CriticFindingView => {
  const id = f.scenePlanId.trim();
  return {
    severity: f.severity,
    severityLabel: has(SEVERITY_TEXT, f.severity) ? SEVERITY_TEXT[f.severity as C2Severity] : String(f.severity),
    codeLabel: has(FINDING_CODE_TEXT, f.code) ? FINDING_CODE_TEXT[f.code] : f.code,
    where: id ? sceneLabel(id, scenes) : 'Cả tập',
    sceneId: id || null,
    message: f.message,
    evidence: f.evidence,
  };
};

const unknownReasonText = (reason: unknown): string => (typeof reason === 'string' && reason ? `lý do chưa biết: ${reason}` : 'không rõ lý do');
export const uncheckedReasonText = (reason: unknown): string => (has(UNCHECKED_REASON_TEXT, reason) ? UNCHECKED_REASON_TEXT[reason as C2UncheckedReason] : unknownReasonText(reason));
export const repairRejectText = (reason: unknown): string => (has(REPAIR_REJECT_TEXT, reason) ? REPAIR_REJECT_TEXT[reason as C2RepairRejectReason] : unknownReasonText(reason));
export const findingRejectText = (reason: unknown): string => (has(FINDING_REJECT_TEXT, reason) ? FINDING_REJECT_TEXT[reason as C2RejectReason] : unknownReasonText(reason));

/** Khối `critic` hợp lệ của biên lai v2 (hoặc null). Chỉ v2 + status đã biết. */
export const criticBlockOf = (rec: Pick<EpisodeScriptRecord, 'script'> | null | undefined): QualityReviewCritic | null => {
  const qr: unknown = rec?.script?.qualityReview;
  if (!isObj(qr) || qr.version !== 2 || !isObj(qr.critic)) return null;
  const c = qr.critic as unknown as QualityReviewCritic;
  return CRITIC_STATUSES.includes(c.status) ? c : null;
};

/** Ý kiến CÒN LẠI sau lượt cuối: `REPAIRED` ⇒ lượt chấm lại (cả cấp tập); còn lại ⇒ lượt 1 (cả cấp tập). `UNCHECKED` ⇒ không có. */
const remainingOf = (c: QualityReviewCritic): C2Finding[] => {
  if (c.status === 'UNCHECKED') return [];
  if (c.status === 'REPAIRED') return [...findingsOf(c.repair?.findingsAfter), ...findingsOf(c.repair?.unscopedAfter)];
  return [...findingsOf(c.findingsBefore), ...findingsOf(c.unscoped)];
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** «Chấm lại bằng AI lúc HH:mm dd/MM · người» theo giờ máy người xem. Giờ hỏng / thiếu người ⇒ bỏ đúng phần đó. */
export const rejudgeNoteOf = (c: Pick<QualityReviewCritic, 'trigger' | 'checkedAt' | 'rejudgedBy'>): string | null => {
  if (c.trigger !== 'manual_rejudge') return null;
  const t = typeof c.checkedAt === 'string' && c.checkedAt ? new Date(c.checkedAt) : null;
  const when = t && !Number.isNaN(t.getTime()) ? ` lúc ${pad2(t.getHours())}:${pad2(t.getMinutes())} ${pad2(t.getDate())}/${pad2(t.getMonth() + 1)}` : '';
  const who = typeof c.rejudgedBy === 'string' && c.rejudgedBy.trim() ? ` · ${c.rejudgedBy.trim()}` : '';
  return `${REJUDGE_META_PREFIX}${when}${who}`;
};

const metaOf = (c: QualityReviewCritic): { meta: string; modelText: string | null; callsText: string } => {
  const calls = Array.isArray(c.calls) ? c.calls.filter(isObj) : [];
  const rounds = calls.filter((x) => x.phase === 'critic_1' || x.phase === 'critic_2').length;
  const model = typeof c.model === 'string' && c.model ? c.model : null;
  const callsText = `${rounds} lượt chấm`;
  // Biên lai chấm lại: dòng meta MANG luôn biên nhận (giờ · người) ⇒ thẻ vẽ MỘT dòng, không lặp «Chấm lại bằng AI».
  const meta = [rejudgeNoteOf(c) ?? CRITIC_META_PREFIX, model, callsText].filter(Boolean).join(' · ');
  return { meta, modelText: model ? `Giám khảo: ${model}` : null, callsText };
};

/** Số vòng tối đa của nhánh chuỗi (BE `C2_CHAIN_MAX_ROUNDS`) — chỉ để in câu «Đã sửa 2 vòng». */
const CHAIN_MAX_ROUNDS = 2;

/**
 * 261023 E3-P3e (L13) — câu «vì sao máy dừng sửa». `NONE` / hết đích / mã lạ / vắng ⇒ null. `ROUNDS_EXHAUSTED` mà không còn đích ⇒ null (không có gì để nói).
 * Dùng chung ở thẻ ⑤ và Sổ duyệt để hai nơi cùng một chữ.
 */
export const chainStopLine = (stopReason: C2ChainStopReason | string | null | undefined, remainingTargets: number): string | null => {
  const n = Number.isFinite(remainingTargets) && remainingTargets > 0 ? Math.floor(remainingTargets) : 0;
  switch (stopReason) {
    case 'NO_CONVERGENCE': return 'Dừng sửa: không hội tụ';
    case 'ROUNDS_EXHAUSTED': return n > 0 ? `Đã sửa ${CHAIN_MAX_ROUNDS} vòng, còn ${n} lỗi` : null;
    case 'NOTHING_FIXABLE': return 'Máy không tự sửa được: chỉ còn lỗi ngoài danh sách lỗi máy được phép sửa';
    default: return null;
  }
};

export interface ChainRoundView { label: string; text: string; accepted: boolean }

/** 261023 E3-P3e — một dòng mỗi vòng tự sửa từ `chain.rounds`. Phần tử hỏng hình ⇒ bỏ (không ném). */
export const chainRoundsOf = (chain: C2ChainReceipt | null | undefined, scenes: SceneRefs): ChainRoundView[] => {
  if (!isObj(chain) || !Array.isArray(chain.rounds)) return [];
  const out: ChainRoundView[] = [];
  for (const r of chain.rounds as unknown[]) {
    if (!isObj(r) || typeof r.round !== 'number' || typeof r.accepted !== 'boolean') continue;
    const labels = (ids: unknown) => strs(ids).map((id) => sceneLabel(id, scenes)).join(', ');
    const score = typeof r.scoreBefore === 'number' && typeof r.scoreAfter === 'number' ? ` · điểm lỗi ${r.scoreBefore} → ${r.scoreAfter}` : '';
    if (r.accepted) {
      const changed = labels(r.changedScenes);
      out.push({ label: `Vòng ${r.round}`, text: `nhận bản sửa${changed ? ` · ${changed}` : ''}${score}`, accepted: true });
    } else {
      const tried = labels(r.targetScenes);
      out.push({ label: `Vòng ${r.round}`, text: `bỏ bản sửa (${repairRejectText(r.rejectReason)})${tried ? ` · thử ${tried}` : ''}`, accepted: false });
    }
  }
  return out;
};

/**
 * Dựng thẻ ⑤. `null` ⇔ không có gì để nói về giám khảo (bản v1 / vắng critic / hình hỏng) — KHÔNG hiện dòng nào, đặc biệt KHÔNG "đạt".
 * `violations` = danh sách GET (đã có chip `CRITIC_FINDING` tính lúc đọc) — nguồn để biết ý kiến nào thuộc cảnh đã đổi.
 */
export const criticCardOf = (
  rec: Pick<EpisodeScriptRecord, 'script' | 'violations' | 'criticFreshness'> | null | undefined,
): CriticCard | null => {
  const c = criticBlockOf(rec);
  if (!c) return null;
  const scenes = rec?.script?.scenes;
  const view = (f: C2Finding): CriticFindingView => findingView(f, scenes);
  const remaining = remainingOf(c);
  const criticalLeft = remaining.filter(isCritical).length;
  const lessSevere = remaining.length - criticalLeft;
  const firstPassAll = c.status === 'UNCHECKED' ? [] : [...findingsOf(c.findingsBefore), ...findingsOf(c.unscoped)];
  const repair = isObj(c.repair) ? c.repair : undefined;
  const changed = strs(repair?.changedScenes);
  const targets = strs(repair?.targetScenes);
  const skipped = strs(repair?.skippedScenes);
  const { meta, modelText, callsText } = metaOf(c);

  let tone: CriticCardTone = 'ok';
  let headline = '';
  let detail: string | null = null;
  switch (c.status) {
    case 'PASSED': {
      // 261015 P5d (D3): status BE giữ nguyên (C2 chỉ chặn/sửa CRITICAL), nhưng còn MAJOR thì thẻ KHÔNG được xanh "không thấy lỗi nặng" — pill vàng + nói rõ còn N lỗi đáng kể.
      const majorLeft = remaining.filter((x) => x.severity === 'MAJOR').length;
      if (majorLeft > 0) {
        tone = 'warn';
        headline = `Không có lỗi nặng (CRITICAL) nhưng còn ${majorLeft} lỗi đáng kể (MAJOR)`;
        const minorLeft = lessSevere - majorLeft;
        detail = minorLeft > 0 ? `Và ${minorLeft} ý kiến mức nhẹ — xem trong danh sách cảnh báo` : 'Xem trong danh sách cảnh báo';
      } else {
        headline = 'Giám khảo không thấy lỗi nặng';
        detail = lessSevere > 0 ? `Còn ${lessSevere} ý kiến mức nhẹ/vừa (không chặn) — xem trong danh sách cảnh báo` : null;
      }
      break;
    }
    case 'REPAIRED': {
      // N = số cảnh THẬT SỰ đổi trong bản được nhận (thiếu mảng ⇒ lùi về số cảnh đích). "đạt" chỉ khi lượt chấm lại CÓ dữ liệu và hết CRITICAL.
      const n = Array.isArray(repair?.changedScenes) ? changed.length : targets.length;
      const rechecked = Array.isArray(repair?.findingsAfter);
      if (repair?.accepted !== true) {
        // REPAIRED mà biên lai không ghi `accepted: true` ⇒ không xác nhận được bản sửa đã được nhận ⇒ TUYỆT ĐỐI không "đạt" / "đã sửa".
        tone = 'warn';
        headline = 'Biên lai không xác nhận được bản sửa đã được nhận';
        detail = 'Trạng thái ghi "đã sửa" nhưng biên lai thiếu dấu nhận bản sửa — coi như chưa kiểm';
      } else if (rechecked && criticalLeft === 0 && c.chain && remaining.some((x) => x.severity === 'MAJOR')) {
        // 3e (R1): nhánh chuỗi cũng sửa MAJOR ⇒ hết CRITICAL chưa có nghĩa «đạt» — còn MAJOR thì thẻ vàng, nói rõ còn bao nhiêu.
        const majorLeft = remaining.filter((x) => x.severity === 'MAJOR').length;
        tone = 'warn';
        headline = `Đã sửa ${n} cảnh theo giám khảo — còn ${majorLeft} lỗi đáng kể (MAJOR)`;
        const minorLeft = lessSevere - majorLeft;
        if (minorLeft > 0) detail = `Và ${minorLeft} ý kiến mức nhẹ — xem trong danh sách cảnh báo`;
      } else if (rechecked && criticalLeft === 0) {
        headline = `Đã sửa ${n} cảnh theo giám khảo + chấm lại đạt`;
        if (lessSevere > 0) detail = `Còn ${lessSevere} ý kiến mức nhẹ/vừa (không chặn)`;
      } else if (rechecked) {
        tone = 'warn';
        headline = `Đã sửa ${n} cảnh theo giám khảo — còn ${criticalLeft} lỗi nặng`;
      } else {
        tone = 'warn';
        headline = `Đã sửa ${n} cảnh theo giám khảo`;
        detail = 'Không đọc được kết quả chấm lại';
      }
      break;
    }
    case 'REPAIR_REJECTED':
      tone = 'warn';
      headline = `Giữ bản gốc — bản sửa không đạt (${repairRejectText(repair?.rejectReason)})`;
      detail = criticalLeft > 0 ? `Còn ${criticalLeft} lỗi nặng ở bản gốc` : null;
      break;
    case 'UNRESOLVED':
      tone = 'warn';
      headline = 'Còn lỗi nặng, chưa tự sửa được';
      detail = criticalLeft > 0 ? `${criticalLeft} lỗi nặng — dùng Trợ lý AI hoặc sửa tay` : null;
      break;
    default: // UNCHECKED
      tone = 'unknown';
      headline = `Chưa kiểm được (${uncheckedReasonText(c.reason)})`;
      detail = 'Không phải "đạt" — tập vẫn viết xong vì các cổng cấu trúc độc lập đã qua';
  }

  const freshness: CriticFreshness | undefined = rec?.criticFreshness;
  let freshnessNote: string | null = null;
  let staleFindings: CriticFindingView[] = [];
  if (freshness === 'PARTLY_CHANGED' || freshness === 'CHANGED') {
    freshnessNote = FRESHNESS_NOTE;
    // Cảnh còn tươi ⇔ BE còn phát chip `CRITIC_FINDING` với `refs` chứa cảnh đó. Chưa có danh sách violations ⇒ không đoán (chỉ giữ dòng ghi chú).
    if (Array.isArray(rec?.violations)) {
      const freshScenes = new Set<string>();
      for (const v of rec!.violations!) {
        if (v?.gate === 'CRITIC_FINDING' && Array.isArray(v.refs)) v.refs.forEach((r) => { if (typeof r === 'string') freshScenes.add(r.trim()); });
      }
      // Finding cấp tập (không cảnh) chỉ có chip khi CẢ TẬP còn tươi ⇒ ở đây (≠ CURRENT) luôn thuộc bản trước.
      staleFindings = remaining.filter((f) => { const id = f.scenePlanId.trim(); return !id || !freshScenes.has(id); }).map(view);
    }
  }

  const rejectedRaw = [...(Array.isArray(c.rejects) ? c.rejects : []), ...(Array.isArray(repair?.rejectsAfter) ? (repair!.rejectsAfter as unknown[]) : [])]
    .filter((r): r is { finding: C2Finding; reason: C2RejectReason } => isObj(r) && findingsOf([r.finding]).length === 1);

  return {
    status: c.status, tone, headline, detail, criticalLeft, meta, rejudgeNote: rejudgeNoteOf(c), freshnessNote, staleFindings,
    details: {
      firstPass: firstPassAll.map(view),
      remaining: remaining.map(view),
      targetScenes: targets.map((id) => sceneLabel(id, scenes)),
      changedScenes: changed.map((id) => sceneLabel(id, scenes)),
      skippedScenes: skipped.map((id) => sceneLabel(id, scenes)),
      rejected: rejectedRaw.map((r) => ({ view: view(r.finding), reasonText: findingRejectText(r.reason) })),
      modelText, callsText,
      rounds: chainRoundsOf(c.chain, scenes),
      chainStop: c.chain ? chainStopLine(c.chain.stopReason, c.chain.remainingTargets) : null,
    },
  };
};
